/**
 * 导演模式管理后台接口
 */

import { PlatformApiError, platformFetch, readEnvelope } from '../../../platform/http';
import type { DirectorComposition, DirectorProject } from './directorApi';

class AdminApiError extends PlatformApiError {
  constructor(message: string, status: number) {
    super(message, status);
    this.name = 'AdminApiError';
  }
}

async function authFetch<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  try {
    return await readEnvelope<T>(
      await platformFetch(path, options),
      (status) => `请求失败 (${status})`
    );
  } catch (error) {
    if (error instanceof PlatformApiError && !(error instanceof AdminApiError)) {
      throw new AdminApiError(error.message, error.status);
    }
    throw error;
  }
}

// ============================================================================
// 类型（管理员视角，含 errorMessage 等敏感字段）
// ============================================================================

export interface AdminDirectorProject extends DirectorProject {
  user: { id: string; name: string | null; email: string };
}

export interface AdminDirectorComposition extends DirectorComposition {
  errorMessage: string | null;
  project: { id: string; name: string; userId: string };
}

export interface DirectorSystemConfig {
  maxShotsPerProject: number;
  maxKeyframeCandidatesPerUser: number;
  maxCompositionsPerUser: number;
  shotSplitRateLimitPerMinute: number;
  compositionTimeoutSeconds: number;
  ffmpegStepTimeoutSeconds: number;
  compositionWorkerIntervalMs: number;
  shotSplitCreditsPerCall: number;
  siteWatermarkEnabled: boolean;
  siteWatermarkLogoStorageKey: string | null;
  fontPath: string | null;
  shareLinkDefaultExpiresInDays: number;
  characterAngleTemplates: string;
  propAngleTemplates: string;
  sceneAngleTemplates: string;
  shotSplitLlmEnabled: boolean;
  shotSplitLlmModelId: string | null;
  shotSplitLlmSystemPrompt: string | null;
}

export interface ShotSplitConfig {
  shotSplitLlmEnabled: boolean;
  shotSplitLlmModelId: string | null;
  shotSplitLlmSystemPrompt: string | null;
  shotSplitCreditsPerCall: number;
  shotSplitRateLimitPerMinute: number;
}

export interface FrameStoryboardConfig {
  frameStoryboardLlmEnabled: boolean;
  frameStoryboardLlmModelId: string | null;
  frameStoryboardLlmSystemPrompt: string | null;
  frameStoryboardCreditsPerCall: number;
  frameStoryboardRateLimitPerMinute: number;
}

export interface TrinityCardsConfig {
  trinityCardsLlmEnabled: boolean;
  trinityCardsLlmModelId: string | null;
  trinityCardsLlmSystemPrompt: string | null;
  trinityCardsCreditsPerCall: number;
  trinityCardsRateLimitPerMinute: number;
  trinityCardsPromptTemplates: {
    role: string;
    story: string;
    video: string;
  };
}

export interface ModeSwitchConfig {
  keyframeEnabled: boolean;
  frameSequenceEnabled: boolean;
  trinityCardsEnabled: boolean;
}

export interface AngleTemplate {
  name: string;
  promptSuffix: string;
}

export interface PromptTemplateItem {
  label: string;
  text: string;
}

export interface PromptTemplateGroup {
  groupLabel: string;
  groupHint?: string;
  templates: PromptTemplateItem[];
}

// ============================================================================
// API
// ============================================================================

export const directorAdminApi = {
  // 项目
  listAllProjects(params: { page?: number; pageSize?: number; userId?: string; status?: string } = {}) {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    if (params.userId) q.set('userId', params.userId);
    if (params.status) q.set('status', params.status);
    return authFetch<{
      data: AdminDirectorProject[];
      pagination: { page: number; pageSize: number; total: number };
    }>(`/admin/director/projects?${q.toString()}`);
  },
  deleteProject(projectId: string) {
    return authFetch<void>(`/admin/director/projects/${projectId}`, { method: 'DELETE' });
  },

  // 合成任务
  listCompositions(params: { page?: number; pageSize?: number; status?: string } = {}) {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    if (params.status) q.set('status', params.status);
    return authFetch<{
      data: AdminDirectorComposition[];
      pagination: { page: number; pageSize: number; total: number };
    }>(`/admin/director/compositions?${q.toString()}`);
  },
  getComposition(id: string) {
    return authFetch<AdminDirectorComposition>(`/admin/director/compositions/${id}`);
  },
  retryComposition(id: string) {
    return authFetch<DirectorComposition>(`/admin/director/compositions/${id}/retry`, { method: 'POST' });
  },

  // 系统配置
  getSystemConfig() {
    return authFetch<DirectorSystemConfig>('/admin/director/system-config');
  },
  updateSystemConfig(patch: Partial<DirectorSystemConfig>) {
    return authFetch<DirectorSystemConfig>('/admin/director/system-config', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },

  // LLM 拆分镜配置
  getShotSplitConfig() {
    return authFetch<ShotSplitConfig>('/admin/director/shot-split-config');
  },
  updateShotSplitConfig(patch: Partial<ShotSplitConfig>) {
    return authFetch<void>('/admin/director/shot-split-config', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },

  // LLM 多帧 prompt 助手配置
  getFrameStoryboardConfig() {
    return authFetch<FrameStoryboardConfig>('/admin/director/frame-storyboard-config');
  },
  updateFrameStoryboardConfig(patch: Partial<FrameStoryboardConfig>) {
    return authFetch<void>('/admin/director/frame-storyboard-config', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },

  // LLM 三合一卡片配置
  getTrinityCardsConfig() {
    return authFetch<TrinityCardsConfig>('/admin/director/trinity-cards-config');
  },
  updateTrinityCardsConfig(patch: Partial<TrinityCardsConfig>) {
    return authFetch<void>('/admin/director/trinity-cards-config', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },

  // 角色多视角模板
  getAngleTemplates() {
    return authFetch<AngleTemplate[]>('/admin/director/character-angle-templates');
  },
  updateAngleTemplates(templates: AngleTemplate[]) {
    return authFetch<AngleTemplate[]>('/admin/director/character-angle-templates', {
      method: 'PUT',
      body: JSON.stringify({ templates }),
    });
  },

  // 物品视角模板
  getPropAngleTemplates() {
    return authFetch<AngleTemplate[]>('/admin/director/prop-angle-templates');
  },
  updatePropAngleTemplates(templates: AngleTemplate[]) {
    return authFetch<AngleTemplate[]>('/admin/director/prop-angle-templates', {
      method: 'PUT',
      body: JSON.stringify({ templates }),
    });
  },

  // 场景视角模板
  getSceneAngleTemplates() {
    return authFetch<AngleTemplate[]>('/admin/director/scene-angle-templates');
  },
  updateSceneAngleTemplates(templates: AngleTemplate[]) {
    return authFetch<AngleTemplate[]>('/admin/director/scene-angle-templates', {
      method: 'PUT',
      body: JSON.stringify({ templates }),
    });
  },

  // 导演模式开关配置
  getModeSwitchConfig() {
    return authFetch<ModeSwitchConfig>('/admin/director/mode-switch-config');
  },
  updateModeSwitchConfig(patch: Partial<ModeSwitchConfig>) {
    return authFetch<ModeSwitchConfig>('/admin/director/mode-switch-config', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },

  // 导演模式提示词模板
  getPromptTemplateGroups() {
    return authFetch<PromptTemplateGroup[]>('/admin/director/prompt-template-groups');
  },
  updatePromptTemplateGroups(groups: PromptTemplateGroup[]) {
    return authFetch<PromptTemplateGroup[]>('/admin/director/prompt-template-groups', {
      method: 'PUT',
      body: JSON.stringify({ groups }),
    });
  },
};
